import {
  FirmaDigitalDocumento,
  FirmaDigitalDocumentoFirmante,
} from '../crm/expedientes/expedientes/detail/firma-digital-documents.mock';

export type FirmaDigitalGlobalEstatus = FirmaDigitalDocumento['estatusGlobal'];

export interface FirmaDigitalDocumentoGlobal extends FirmaDigitalDocumento {
  tipo: 'Venta' | 'General';
  solicitante: string;
}

const PDF_MOCK = '/docs/Contrato_Preventa_Departamento.pdf';

type FirmanteInput = {nombre: string; rol: string};

function buildFirmantes(
  estatusGlobal: FirmaDigitalDocumento['estatusGlobal'],
  fechaSolicitud: string,
  firmantes: FirmanteInput[],
): FirmaDigitalDocumentoFirmante[] {
  if (estatusGlobal === 'Firma completa') {
    return firmantes.map((firmante, index) => ({
      ...firmante,
      estatus: 'Firmado' as const,
      firmadoEl: shiftMinutes(fechaSolicitud, 30 + index * 45),
    }));
  }

  if (estatusGlobal === 'Firma parcial') {
    return firmantes.map((firmante, index) =>
      index === 0
        ? {
            ...firmante,
            estatus: 'Firmado' as const,
            firmadoEl: shiftMinutes(fechaSolicitud, 35),
          }
        : {...firmante, estatus: 'Pendiente' as const},
    );
  }

  return firmantes.map(firmante => ({...firmante, estatus: 'Pendiente' as const}));
}

function shiftMinutes(fechaSolicitud: string, minutes: number): string {
  const parsed = parseDateTime(fechaSolicitud);
  const date = new Date(parsed + minutes * 60_000);
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDateTime(value: string): number {
  const [datePart, timePart = '00:00'] = value.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes).getTime();
}

export const MOCK_FIRMA_DIGITAL_GLOBAL: FirmaDigitalDocumentoGlobal[] = [
  {
    id: '1',
    nombre: 'Contrato promesa de compraventa',
    fechaSolicitud: '18/06/2026 09:15',
    estatusGlobal: 'Pendiente',
    tipo: 'Venta',
    solicitante: 'Ana Sofía Ramírez Torres',
    pdfUrl: null,
    firmantes: buildFirmantes('Pendiente', '18/06/2026 09:15', [
      {nombre: 'Ana Sofía Ramírez Torres', rol: 'Cliente'},
      {nombre: 'Mariel Karely Cázares Navarro', rol: 'Copropietario'},
      {nombre: 'Carlos Alberto Méndez Ruiz', rol: 'Representante legal'},
    ]),
  },
  {
    id: '2',
    nombre: 'Autorización de tratamiento de datos',
    fechaSolicitud: '17/06/2026 11:40',
    estatusGlobal: 'Pendiente',
    tipo: 'General',
    solicitante: 'Luis Fernando Delgado Pérez',
    pdfUrl: null,
    firmantes: buildFirmantes('Pendiente', '17/06/2026 11:40', [
      {nombre: 'Luis Fernando Delgado Pérez', rol: 'Cliente'},
      {nombre: 'Patricia Morales Vega', rol: 'Testigo'},
    ]),
  },
  {
    id: '3',
    nombre: 'Autorización de cargo domiciliado',
    fechaSolicitud: '02/06/2026 10:20',
    estatusGlobal: 'Solicitado',
    tipo: 'Venta',
    solicitante: 'Mariel Karely Cázares Navarro',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Solicitado', '02/06/2026 10:20', [
      {nombre: 'Mariel Karely Cázares Navarro', rol: 'Cliente'},
    ]),
  },
  {
    id: '4',
    nombre: 'Carta responsiva de entrega',
    fechaSolicitud: '01/06/2026 16:05',
    estatusGlobal: 'Solicitado',
    tipo: 'General',
    solicitante: 'Roberto Hernández Pérez',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Solicitado', '01/06/2026 16:05', [
      {nombre: 'Roberto Hernández Pérez', rol: 'Cliente'},
      {nombre: 'Juan Pérez González', rol: 'Testigo'},
      {nombre: 'Lucía Fernanda Torres Ruiz', rol: 'Copropietario'},
    ]),
  },
  {
    id: '5',
    nombre: 'Anexo de calendario de pagos',
    fechaSolicitud: '06/06/2026 11:45',
    estatusGlobal: 'Firma parcial',
    tipo: 'Venta',
    solicitante: 'Ana Sofía Ramírez Torres',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Firma parcial', '06/06/2026 11:45', [
      {nombre: 'Ana Sofía Ramírez Torres', rol: 'Cliente'},
      {nombre: 'Mariel Karely Cázares Navarro', rol: 'Copropietario'},
    ]),
  },
  {
    id: '6',
    nombre: 'Convenio de confidencialidad',
    fechaSolicitud: '05/06/2026 08:30',
    estatusGlobal: 'Firma parcial',
    tipo: 'General',
    solicitante: 'Carlos Alberto Méndez Ruiz',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Firma parcial', '05/06/2026 08:30', [
      {nombre: 'Carlos Alberto Méndez Ruiz', rol: 'Cliente'},
      {nombre: 'Patricia Morales Vega', rol: 'Representante legal'},
    ]),
  },
  {
    id: '7',
    nombre: 'Carta de instrucción de escrituración',
    fechaSolicitud: '14/06/2026 08:30',
    estatusGlobal: 'Firma completa',
    tipo: 'Venta',
    solicitante: 'Ana Sofía Ramírez Torres',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Firma completa', '14/06/2026 08:30', [
      {nombre: 'Ana Sofía Ramírez Torres', rol: 'Cliente'},
      {nombre: 'Carlos Alberto Méndez Ruiz', rol: 'Representante legal'},
    ]),
  },
  {
    id: '8',
    nombre: 'Aviso de privacidad y tratamiento de datos',
    fechaSolicitud: '10/06/2026 14:00',
    estatusGlobal: 'Firma completa',
    tipo: 'General',
    solicitante: 'Mariel Karely Cázares Navarro',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Firma completa', '10/06/2026 14:00', [
      {nombre: 'Mariel Karely Cázares Navarro', rol: 'Cliente'},
    ]),
  },
  {
    id: '9',
    nombre: 'Acuse de integración de expediente',
    fechaSolicitud: '28/05/2026 13:10',
    estatusGlobal: 'Firma completa',
    tipo: 'Venta',
    solicitante: 'Roberto Hernández Pérez',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Firma completa', '28/05/2026 13:10', [
      {nombre: 'Roberto Hernández Pérez', rol: 'Cliente'},
      {nombre: 'Carlos Alberto Méndez Ruiz', rol: 'Representante legal'},
      {nombre: 'Juan Pérez González', rol: 'Testigo'},
    ]),
  },
  {
    id: '10',
    nombre: 'Contrato de compraventa cancelado',
    fechaSolicitud: '22/05/2026 09:50',
    estatusGlobal: 'Cancelado',
    tipo: 'Venta',
    solicitante: 'Lucía Fernanda Torres Ruiz',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Cancelado', '22/05/2026 09:50', [
      {nombre: 'Lucía Fernanda Torres Ruiz', rol: 'Cliente'},
      {nombre: 'Patricia Morales Vega', rol: 'Copropietario'},
    ]),
  },
  {
    id: '11',
    nombre: 'Formato de autorización biométrica',
    fechaSolicitud: '19/05/2026 15:22',
    estatusGlobal: 'Cancelado',
    tipo: 'General',
    solicitante: 'Luis Fernando Delgado Pérez',
    pdfUrl: PDF_MOCK,
    firmantes: buildFirmantes('Cancelado', '19/05/2026 15:22', [
      {nombre: 'Luis Fernando Delgado Pérez', rol: 'Cliente'},
    ]),
  },
];

export const FIRMA_DIGITAL_SIDEBAR_STATUSES: {label: string; value: FirmaDigitalGlobalEstatus}[] = [
  {label: 'Pendientes', value: 'Pendiente'},
  {label: 'Solicitud de firma', value: 'Solicitado'},
  {label: 'Firma parcial', value: 'Firma parcial'},
  {label: 'Firma completa', value: 'Firma completa'},
  {label: 'Cancelados', value: 'Cancelado'},
];

export const FIRMA_DIGITAL_STATUS_COLORS: Record<FirmaDigitalGlobalEstatus, string> = {
  Pendiente: '#5a5a5a',
  Solicitado: '#1a5fa0',
  'Firma parcial': '#92400e',
  'Firma completa': '#1e7a35',
  Cancelado: '#991b1b',
};
